import { mdiChevronRight } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';

import { Icon } from 'common/components/Icon';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemMediaDate,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { Link } from 'common/components/Link/Link';
import { getOfferDateDisplay } from 'offer/utils/dates';
import {
    IFlatOfferEventDateFragment,
    IOfferEventListItemFragment,
    IOfferEventSubscriptionStatus,
    IOfferEventType,
} from 'graphql/types';
import { Chip, ChipGroup } from 'common/components/Chip';
import { getLabelTranslation } from 'offer/utils/offerEvent';
import { formatRoundPrice } from 'common/utils/price';
import { OfferPermissionsContext } from 'offer/contexts';
import { OfferEventPermissionsContext } from 'offer/contexts/OfferPermissionsContext';

import { OfferEventSubscriptionStatusChip } from '../OfferEventSubscriptionStatusChip';

interface IProps extends IOfferEventListItemFragment {
    description?: React.ReactNode;
    disabled?: boolean;
    onClick?(): void;
}

export const OfferEventListItem = ({
    closingDateExpired,
    currentUserEnrollment,
    dates,
    dateCount,
    firstDate,
    price,
    spotsLeft,
    subscriptionStatus,
    type,
    firstTraining,
    trainingCount,
    publish,
    accessCourses,
    waitingList,
    maxSubscriptions,
    title,
    cancelled,
    disabled,
    image,
    onClick,
    labels,
}: IProps) => {
    const { canUpdate = false } = useContext(OfferPermissionsContext) || {};
    const { canManageSubscriptions = false } =
        useContext(OfferEventPermissionsContext) || {};
    const [translate] = useTranslation();

    const isTraining = type === IOfferEventType.Training;
    const isCourse = type === IOfferEventType.Course;

    let itemTitle = !title && translate('startNow');

    if (!isTraining && !isCourse && firstDate) {
        itemTitle = getOfferDateDisplay(firstDate, dateCount, true, true);
    }

    if (isTraining && firstTraining) {
        itemTitle = `${firstTraining.title} ${
            trainingCount && trainingCount > 1
                ? ` | ${translate('otherTrainings', {
                      count: trainingCount - 1,
                  })}`
                : ''
        }`;
    }

    const firstCourse = accessCourses?.edges?.[0]?.node;

    if (isCourse && !!firstCourse) {
        const courseCount = accessCourses?.count;

        itemTitle = `${firstCourse.title} ${
            courseCount && courseCount > 1
                ? ` | ${translate('otherCourses', {
                      count: courseCount - 1,
                  })}`
                : ''
        }`;
    }

    const showClosingDateExpiredChip =
        closingDateExpired &&
        (!currentUserEnrollment || canUpdate || canManageSubscriptions);

    const spotsAmount = !!spotsLeft && spotsLeft > 0 ? spotsLeft : 0;
    const currentUserOnWaitingList =
        currentUserEnrollment?.status === IOfferEventSubscriptionStatus.Full &&
        waitingList;

    const canManage = canUpdate || canManageSubscriptions;

    const locations = dates
        ?.map((date) => date?.location?.title || date.locationString)
        .filter(Boolean);
    const firstLocation = locations?.[0];
    const hasMultipleLocations = locations?.some(
        (location) => location !== firstLocation
    );

    const offerChips = (
        <ChipGroup>
            {firstLocation && (
                <Chip
                    label={
                        !hasMultipleLocations
                            ? firstLocation
                            : translate('moreLocations', {
                                  location: firstLocation,
                              })
                    }
                />
            )}

            {labels.map((label, index) => (
                <Chip
                    bgColor="secondary"
                    key={`${label}-${index}`}
                    label={getLabelTranslation(label)}
                />
            ))}

            {!!price && !!Number(price) && (
                <Chip
                    label={translate('price', {
                        price: formatRoundPrice(price),
                    })}
                />
            )}

            {currentUserOnWaitingList && (
                <Chip
                    bgColor="warning"
                    label={translate('waitingListSubscriptionLabel')}
                />
            )}

            {waitingList &&
                ((!currentUserOnWaitingList &&
                    !spotsAmount &&
                    !currentUserEnrollment &&
                    maxSubscriptions > 0) ||
                    canManage) && (
                    <Chip
                        bgColor="warning"
                        label={translate('waitingListLabel')}
                    />
                )}

            {!!spotsAmount && !subscriptionStatus && (
                <Chip label={translate('spotsLeft', { count: spotsAmount })} />
            )}

            {!spotsAmount && maxSubscriptions > 0 && !currentUserEnrollment && (
                <Chip bgColor="warning" label={translate('full')} />
            )}

            {!!subscriptionStatus && (
                <OfferEventSubscriptionStatusChip
                    subscriptionStatus={subscriptionStatus}
                />
            )}

            {!publish && (
                <Chip bgColor="warning" label={translate('concept')} />
            )}

            {showClosingDateExpiredChip && (
                <Chip
                    bgColor="warning"
                    label={translate('subscriptionClosed')}
                />
            )}

            {cancelled && (
                <Chip bgColor="error" label={translate('cancelled')} />
            )}
        </ChipGroup>
    );

    const listItemMediaImage = !!image?.url ? image.url : undefined;

    let mediaDate: IFlatOfferEventDateFragment | undefined;
    let listItemMediaContent;

    if (!isTraining && !isCourse) {
        mediaDate = firstDate || undefined;

        // If first date is not set, use the first date from the dates list
        if (!mediaDate) {
            const date = dates?.[0];

            mediaDate = !!date
                ? {
                      __typename: date.__typename,
                      startDate: date.startDate,
                      endDate: date.endDate,
                  }
                : undefined;
        }

        listItemMediaContent = (
            <ListItemMediaDate
                color="primary"
                date={mediaDate?.startDate}
                size={!!listItemMediaImage ? 'small' : undefined}
            />
        );
    }

    return (
        <ListItem
            button
            component={Link}
            px={0}
            py={1.5}
            sx={{ opacity: disabled ? 0.5 : 1 }}
            onClick={onClick}
        >
            <ListItemMedia
                color="primary"
                image={listItemMediaImage}
                isDate={!!mediaDate}
                sx={{
                    minHeight: { xs: '80px', sm: '106px' },
                    minWidth: { xs: '113px', sm: '150px' },
                }}
            >
                {(!!mediaDate || !listItemMediaImage) && listItemMediaContent}
            </ListItemMedia>

            <ListItemText
                primary={title || itemTitle}
                secondary={!title ? undefined : itemTitle}
            >
                {offerChips}
            </ListItemText>

            <ListItemActionText>{translate('open')}</ListItemActionText>

            <ListItemSecondaryAction hideXs>
                <Icon path={mdiChevronRight} size="4rem" />
            </ListItemSecondaryAction>
        </ListItem>
    );
};
